import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  FormControlLabel,
  MenuItem,
  PaginationItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Checkbox,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { IPowerlistHeaderMapping } from 'src/lib/kixie-entities/tables/Powerlist';
import React from 'react';

/* eslint-disable no-unused-vars */
enum KixieField {
  Ignore = '',
  PhoneNumber = 'Phone Number *',
  Email = 'Email',
  FirstName = 'First Name',
  LastName = 'Last Name',
  Title = 'Title',
  CompanyName = 'Company Name',
  NextCallRefresher = 'Next Call Refresher',
  CRMUserId = 'CRM User Id',
  CRMContactId = 'CRM Contact ID',
  Custom1 = 'Custom1',
  Custom2 = 'Custom2',
  Custom3 = 'Custom3',
  Custom4 = 'Custom4',
  Custom5 = 'Custom5',
  Custom6 = 'Custom6',
}

interface IKixieLabeledField {
  value: string;
  label: string;
  matches: string[];
  allowPartialMatches: boolean;
  multiple?: boolean;
}

interface IContactMapping {
  property: string;
  kixieField: KixieField;
}

type ContactMapping = IPowerlistHeaderMapping | IContactMapping[];

const useStyles = makeStyles((theme: Theme) => ({
  red: {
    color: 'red',
  },
  mappingTableContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  mappingTable: {
    margin: 'auto',
    maxWidth: '100%',
    border: '1px solid #DBDBDB',
    borderRadius: '8px',
    borderCollapse: 'separate',
  },
  mappingTableMargins: {
    margin: 'auto',
    width: '2.5%',
    padding: '22px 0',
  },
  mappingTableMarginsTopLeft: {
    margin: 'auto',
    width: '2.5%',
    borderTopLeftRadius: '8px',
    borderCollapse: 'separate',
    padding: '8px 0',
    backgroundColor: '#E8E8E8',
  },
  mappingTableMarginsTopRight: {
    margin: 'auto',
    width: '2.5%',
    borderTopRightRadius: '8px',
    borderCollapse: 'separate',
    padding: '8px 0',
    backgroundColor: '#E8E8E8',
  },
  mappingHeaderRow: {
    height: 36,
    borderRadius: '8px 8px 0 0',
    borderBottom: '1px solid #DBDBDB',
    backgroundColor: '#E8E8E8',
    '& th': {
      height: 36,
      padding: '8px 16px',
      textAlign: 'left',
    },
  },
  mappingUploadInfoHeader: {
    display: 'table-cell',
    textAlign: 'center',
    width: '33%',
    flex: 1,
    padding: '4px 16px',
  },
  mappingBodyContainer: {
  },
  mappedUploadRow: {
    // border: '0.5px solid #DBDBDB',
  },
  unmappedUploadRow: {
    // border: '0.5px solid #DBDBDB',
  },
  previewRow: {
    border: '0.5px solid #DBDBDB',
    '&:nth-of-type(even)': {
    },
  },
  mappingUploadInfoCell: {
    display: 'table-cell',
    textAlign: 'left',
    width: '33%',
    flex: 1,
    fontSize: '12px',
    padding: '22px 16px',
  },
  mappingSelectField: {
    textAlign: 'left',
    width: '100%',
  },
  mappingUploadIconColumn: {
    display: 'table-cell',
    textAlign: 'center',
    flex: 1,
    padding: '22px 8px',
  },
  warningIcon: {
    color: 'orange',
  },
}));

const KixieLabeledField: Record<KixieField, IKixieLabeledField> = {
  [KixieField.Ignore]: {
    value: KixieField.Ignore,
    label: '(Ignore)',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.PhoneNumber]: {
    value: KixieField.PhoneNumber,
    label: 'Phone Number *',
    matches: ['phone number', 'phone', 'mobile'],
    allowPartialMatches: true,
    multiple: false,
  },
  [KixieField.Email]: {
    value: KixieField.Email,
    label: 'Email',
    matches: ['e-mail', 'mail'],
    allowPartialMatches: true,
  },
  [KixieField.FirstName]: {
    value: KixieField.FirstName,
    label: 'First Name',
    matches: ['forename', 'fname'],
    allowPartialMatches: false,
  },
  [KixieField.LastName]: {
    value: KixieField.LastName,
    label: 'Last Name',
    matches: ['name', 'surname', 'lname'],
    allowPartialMatches: false,
  },
  [KixieField.Title]: {
    value: KixieField.Title,
    label: 'Title',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.CompanyName]: {
    value: KixieField.CompanyName,
    label: 'Company Name',
    matches: ['company'],
    allowPartialMatches: false,
  },
  [KixieField.NextCallRefresher]: {
    value: KixieField.NextCallRefresher,
    label: 'Next Call Refresher',
    matches: ['note', 'notes', 'refresher'],
    allowPartialMatches: false,
  },
  [KixieField.CRMUserId]: {
    value: KixieField.CRMUserId,
    label: 'CRM User Id',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.CRMContactId]: {
    value: KixieField.CRMContactId,
    label: 'CRM Contact ID',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom1]: {
    value: KixieField.Custom1,
    label: 'Custom1',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom2]: {
    value: KixieField.Custom2,
    label: 'Custom2',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom3]: {
    value: KixieField.Custom3,
    label: 'Custom3',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom4]: {
    value: KixieField.Custom4,
    label: 'Custom4',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom5]: {
    value: KixieField.Custom5,
    label: 'Custom5',
    matches: [],
    allowPartialMatches: false,
  },
  [KixieField.Custom6]: {
    value: KixieField.Custom6,
    label: 'Custom6',
    matches: [],
    allowPartialMatches: false,
  },
};

const isOptionVisible = (mapping: ContactMapping, value: string) =>
  value === KixieField.Ignore ||
  Object.values(KixieLabeledField).find((f) => f.value === value)?.multiple ||
  !Object.values(mapping).includes(value);

const MappingTool = ({
  numHeadersUploaded,
  numHeadersWithNoMappings,
  mapping,
  useHeaderRowAsData,
  setUseHeaderRowAsData,
  updateMapping,
  previewRows,
  dateTimeFlags,
  toggleDateTimeFlag,
  originalHeaders,
}: {
  numHeadersUploaded: number;
  numHeadersWithNoMappings: number;
  mapping: ContactMapping;
  useHeaderRowAsData: boolean;
  updateMapping: (_event: any, element: any) => void;
  setUseHeaderRowAsData: (value: boolean) => void;
  previewRows: String[][];
  dateTimeFlags: Record<string, boolean>;
  toggleDateTimeFlag: (header: string) => void;
  originalHeaders: String[];
}) => {
  const classes = useStyles();
  
  // Helper function to get the correct preview value for a header
  // This handles duplicate headers by finding the actual column index in originalHeaders
  const getPreviewValue = (inputHeader: string): String | undefined => {
    // Find the index of this header in the original headers array
    // This gives us the actual column position in the data row
    const originalIndex = originalHeaders.findIndex(
      (h) => h?.toString?.()?.trim?.() === inputHeader
    );
    if (originalIndex === -1) return undefined;
    return previewRows[0]?.[originalIndex];
  };

  return (
    <div>
      <div>
        <p style={{ marginBottom: 0, fontSize: '16px', fontWeight: 700 }}>
          Welcome to the PowerList Mapping Tool
        </p>
        <p style={{ marginBottom: 0 }}>
          Please match the headers from your upload to the correct Kixie
          headers.
        </p>
        <p className={classes.red} style={{ marginBottom: 0 }}>
          <b>Required:</b> You must match your file's 'Phone Number' header to
          Kixie's Phone Number header to continue.
        </p>
      </div>

      <div>
        <FormControlLabel
          control={
            <Switch
              checked={useHeaderRowAsData}
              color="primary"
              onChange={() => {
                setUseHeaderRowAsData(!useHeaderRowAsData);
              }}
            />
          }
          label="Turn this on if the first row of your file contains actual data, not column names."
          style={{ width: '100%' }}
        />
      </div>

      {numHeadersUploaded > 0 && numHeadersWithNoMappings > 0 && (
        <div>
          <Alert
            severity="warning"
            sx={{
              border: '1px solid #c28800',
              borderLeft: '1px solid #c28800 !important',
              backgroundColor: '#fff5e5',
              fontSize: '14px',
              lineHeight: '20px',
              alignItems: 'center',
              color: 'black',
              '& .MuiAlert-icon': {
                color: 'orange',
                alignSelf: 'center',
              },
            }}
            icon={<WarningIcon></WarningIcon>}
          >
            <strong>{numHeadersWithNoMappings}</strong> of your uploaded headers
            could not be matched to a Kixie header. Select a Kixie header that
            closely matches with your uploaded headers or set it as{' '}
            <strong>'Custom'</strong>. If a header is not needed, select{' '}
            <strong>'Ignore'</strong>.
          </Alert>
          <br></br>
        </div>
      )}

      {
        <div
          style={{ width: '100%' }}
          className={classes.mappingTableContainer}
        >
          <Table className={classes.mappingTable}>
            <TableHead className={classes.mappingHeaderRow}>
              <TableRow>
                <th className={classes.mappingTableMarginsTopLeft}></th>
                <th className={classes.mappingUploadInfoCell}>
                  Your Uploaded Header
                </th>
                <th className={classes.mappingUploadIconColumn}></th>
                <th className={classes.mappingUploadInfoCell}>Kixie Header</th>
                <th className={classes.mappingUploadIconColumn}></th>
                <th className={classes.mappingUploadInfoCell}>Preview</th>
                <th className={classes.mappingUploadIconColumn}></th>
                <th className={classes.mappingTableMarginsTopRight}></th>
              </TableRow>
            </TableHead>
            <TableBody className={classes.mappingBodyContainer}>
              {numHeadersUploaded > 0 &&
                Object.entries(mapping).map(
                  ([inputHeader, outputHeader], index) => (
                    <TableRow
                      key={`${inputHeader}-${outputHeader}-${index}`}
                      className={
                        outputHeader == ''
                          ? classes.unmappedUploadRow
                          : classes.mappedUploadRow
                      }
                    >
                      <TableCell
                        className={classes.mappingTableMargins}
                      ></TableCell>
                      <TableCell className={classes.mappingUploadInfoCell}>
                        {!useHeaderRowAsData
                          ? inputHeader
                          : `column ${index + 1}`}
                      </TableCell>
                      <TableCell className={classes.mappingUploadIconColumn}>
                        <ArrowForwardIcon></ArrowForwardIcon>
                      </TableCell>
                      <TableCell
                        className={classes.mappingUploadInfoCell}
                        style={
                          outputHeader.toString().startsWith('Custom')
                            ? { paddingTop: '16px', paddingBottom: '10px' }
                            : {}
                        }
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <Select
                            onChange={updateMapping}
                            value={outputHeader}
                            className={classes.mappingSelectField}
                            sx={{
                              borderRadius: '8px',
                              '& .MuiSelect-select': {
                                padding: '10px 14px',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderRadius: '8px',
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  borderRadius: '8px',
                                },
                              },
                            }}
                          >
                            {Object.values(KixieLabeledField).map(
                              ({ value, label }, index) => (
                                <MenuItem
                                  value={value}
                                  selected={outputHeader === value}
                                  key={`kixie-field-${value}-${index}`}
                                  data-input-header={inputHeader}
                                  style={{
                                    display: 'flex',
                                    padding: '10px 16px',
                                  }}
                                  disabled={
                                    value !== KixieField.Ignore &&
                                    value !== KixieField.PhoneNumber && // 8186 allow multiple mappings for phone
                                    Object.values(mapping).includes(value)
                                  }
                                >
                                  {label}
                                </MenuItem>
                              )
                            )}
                          </Select>
                          {outputHeader.toString().startsWith('Custom') && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={!!dateTimeFlags[inputHeader]}
                                  onChange={() => toggleDateTimeFlag(inputHeader)}
                                />
                              }
                              label="Contains date/time values"
                              labelPlacement="end"
                              sx={{
                                margin: 0,
                                alignSelf: 'flex-start',
                                marginLeft: '-9px',
                              }}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={classes.mappingUploadIconColumn}>
                        <DragHandleIcon />
                      </TableCell>
                      <TableCell className={classes.mappingUploadInfoCell}>
                        {outputHeader === ''
                          ? '-'
                          : outputHeader.toString().startsWith('Custom') && dateTimeFlags[inputHeader]
                            ? parseSmartTimestamp(getPreviewValue(inputHeader)?.toString() ?? '')
                            : getPreviewValue(inputHeader)
                        }
                      </TableCell>
                      <TableCell className={classes.mappingUploadIconColumn}>
                        {outputHeader == '' && (
                          <WarningIcon className={classes.warningIcon}></WarningIcon>
                        )}
                      </TableCell>
                      <TableCell
                        className={classes.mappingTableMargins}
                      ></TableCell>
                    </TableRow>
                  )
                )}
            </TableBody>
          </Table>
        </div>
      }
    </div>
  );
};

const PreviewPageNavigator = ({
  previewContactIndex,
  setPreviewContactIndex,
  previewRows,
}: {
  previewContactIndex: number;
  setPreviewContactIndex: (value: number) => void;
  previewRows: String[][];
}) => {
  return (
    <div>
      <PaginationItem
        onClick={() => {
          setPreviewContactIndex(previewContactIndex - 1);
        }}
        color="primary"
        variant="outlined"
        type="previous"
        shape="rounded"
        size="small"
        disabled={previewContactIndex === 0}
      />
      <span>
        {' '}
        {previewContactIndex + 1} of {previewRows.length}{' '}
      </span>
      <PaginationItem
        onClick={() => {
          setPreviewContactIndex(previewContactIndex + 1);
        }}
        color="primary"
        variant="outlined"
        type="next"
        shape="rounded"
        size="small"
        disabled={previewContactIndex === previewRows.length - 1}
      />
    </div>
  );
};

// Helper function to detect if a value looks like an Excel serial number
const isExcelSerialNumber = (value: string | number): boolean => {
  if (typeof value === 'string') {
    // Must be strictly numeric (optionally with decimal) – no dashes/letters
    if (!/^\d+(\.\d+)?$/.test(value.trim())) {
      return false;
    }
    const numValue = parseFloat(value);
    return numValue >= 1 && numValue <= 50000;
  }

  // For numbers, keep previous range check
  const numValue = value as number;
  return numValue >= 1 && numValue <= 50000;
};

// Helper function to parse Excel serial numbers
const parseExcelDate = (value: string | number): string => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  try {
    // Excel serial date starts from January 1, 1900
    // Note: Excel has a bug where it treats 1900 as a leap year, so we need to account for that
    const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
    const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
    return date.toLocaleDateString();
  } catch (e) {
    return value.toString();
  }
};

// Convert date/time values to ISO 8601 format for backend storage
export const convertToISODateTime = (value: string | number): string => {
  if (!value || value === '') return '';

  const strValue = value.toString().trim();

  // Strategy 1: Try Excel serial number first (includes time as decimal)
  if (isExcelSerialNumber(value)) {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    try {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
      return date.toISOString();
    } catch (e) {
      return value.toString();
    }
  }

  // Strategy 2: Try Unix timestamps
  const numValue = parseFloat(strValue);
  if (!isNaN(numValue)) {
    // Unix timestamp in seconds (10 digits)
    if (strValue.length === 10 && numValue >= 946684800 && numValue <= 4102444800) {
      try {
        return new Date(numValue * 1000).toISOString();
      } catch (e) {
        // Continue to other strategies
      }
    }
    // Unix timestamp in milliseconds (13 digits)
    if (strValue.length === 13 && numValue >= 946684800000 && numValue <= 4102444800000) {
      try {
        return new Date(numValue).toISOString();
      } catch (e) {
        // Continue to other strategies
      }
    }
  }

  // Strategy 3: Try parsing as Date and convert to ISO
  try {
    const date = new Date(strValue);
    if (!isNaN(date.getTime()) && date.getFullYear() >= 1900 && date.getFullYear() <= 2100) {
      return date.toISOString();
    }
  } catch (e) {
    // Continue to fallback
  }

  // Strategy 4: If nothing works, return original value
  return strValue;
};

// Smart timestamp parser that includes time information (for display only)
export const parseSmartTimestamp = (value: string | number): string => {
  if (!value || value === '') return '';

  const strValue = value.toString().trim();

  // Strategy 1: Try Excel serial number first (includes time as decimal)
  if (isExcelSerialNumber(value)) {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    try {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + numValue * 24 * 60 * 60 * 1000);
      return date.toLocaleString(); // Include time
    } catch (e) {
      return value.toString();
    }
  }

  // Strategy 2: Try Unix timestamps
  const numValue = parseFloat(strValue);
  if (!isNaN(numValue)) {
    // Unix timestamp in seconds (10 digits)
    if (strValue.length === 10 && numValue >= 946684800 && numValue <= 4102444800) {
      try {
        return new Date(numValue * 1000).toLocaleString();
      } catch (e) {
        // Continue to other strategies
      }
    }
    // Unix timestamp in milliseconds (13 digits)
    if (strValue.length === 13 && numValue >= 946684800000 && numValue <= 4102444800000) {
      try {
        return new Date(numValue).toLocaleString();
      } catch (e) {
        // Continue to other strategies
      }
    }
  }

  // Strategy 3: Try ISO 8601 timestamp formats with time
  const isoWithTimePatterns = [
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // 2023-12-25T14:30:00
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/, // 2023-12-25 14:30:00
  ];

  for (const pattern of isoWithTimePatterns) {
    if (pattern.test(strValue)) {
      try {
        const date = new Date(strValue);
        if (!isNaN(date.getTime()) && date.getFullYear() >= 1900 && date.getFullYear() <= 2100) {
          return date.toLocaleString();
        }
      } catch (e) {
        // Continue to next pattern
      }
    }
  }

  // Strategy 4: Try date patterns with time components
  const dateTimePatterns = [
    // MM/dd/yyyy HH:mm:ss or M/d/yyyy H:mm:ss
    { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})/, format: 'US' },
    // dd/MM/yyyy HH:mm:ss or d/M/yyyy H:mm:ss
    { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})/, format: 'EU' },
  ];

  for (const { pattern, format } of dateTimePatterns) {
    const match = strValue.match(pattern);
    if (match) {
      try {
        let year: number = 0, month: number = 0, day: number = 0;
        const [, p1, p2, p3, hour, minute, second] = match.map(Number);

        if (format === 'US') {
          [month, day, year] = [p1, p2, p3];
        } else {
          [day, month, year] = [p1, p2, p3];
        }

        if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          const date = new Date(year, month - 1, day, hour, minute, second);
          if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
            return date.toLocaleString();
          }
        }
      } catch (e) {
        // Continue to next pattern
      }
    }
  }

  // Strategy 5: Try native Date parsing
  try {
    const nativeDate = new Date(strValue);
    if (!isNaN(nativeDate.getTime()) && nativeDate.getFullYear() >= 1900 && nativeDate.getFullYear() <= 2100) {
      return nativeDate.toLocaleString();
    }
  } catch (e) {
    // Continue to fallback
  }

  // Strategy 6: If nothing works, return original value
  return strValue;
};

// Smart date parser that handles multiple formats (date only)
const parseSmartDate = (value: string | number): string => {
  if (!value || value === '') return '';

  const strValue = value.toString().trim();

  // Strategy 1: Try Excel serial number first
  if (isExcelSerialNumber(value)) {
    return parseExcelDate(value);
  }

  // Strategy 2: Try Unix timestamp (10 digits for seconds, 13 for milliseconds)
  const numValue = parseFloat(strValue);
  if (!isNaN(numValue)) {
    // Unix timestamp in seconds (10 digits) - between 1970 and 2100
    if (strValue.length === 10 && numValue >= 946684800 && numValue <= 4102444800) {
      try {
        return new Date(numValue * 1000).toLocaleDateString();
      } catch (e) {
        // Continue to other strategies
      }
    }
    // Unix timestamp in milliseconds (13 digits)
    if (strValue.length === 13 && numValue >= 946684800000 && numValue <= 4102444800000) {
      try {
        return new Date(numValue).toLocaleDateString();
      } catch (e) {
        // Continue to other strategies
      }
    }
  }

  // Strategy 3: Try common date patterns with regex validation first
  const datePatterns = [
    // MM/dd/yyyy or M/d/yyyy
    { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, format: 'US' },
    // dd/MM/yyyy or d/M/yyyy
    { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, format: 'EU' },
    // yyyy-MM-dd (ISO format)
    { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, format: 'ISO' },
    // yyyy/MM/dd
    { pattern: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, format: 'ISO_SLASH' },
    // MM-dd-yyyy
    { pattern: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, format: 'US_DASH' },
    // dd-MM-yyyy
    { pattern: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, format: 'EU_DASH' },
  ];

  for (const { pattern, format } of datePatterns) {
        const match = strValue.match(pattern);
    if (match) {
      try {
        let year: number = 0, month: number = 0, day: number = 0;

        switch (format) {
          case 'US':
          case 'US_DASH':
            [, month, day, year] = match.map(Number);
            break;
          case 'EU':
          case 'EU_DASH':
            [, day, month, year] = match.map(Number);
            break;
          case 'ISO':
          case 'ISO_SLASH':
            [, year, month, day] = match.map(Number);
            break;
        }

        // Validate date components
        if (year >= 1900 && year <= 2100 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          const date = new Date(year, month - 1, day);
          // Double-check the date is valid (handles edge cases like Feb 30)
          if (date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day) {
            return date.toLocaleDateString();
          }
        }
      } catch (e) {
        // Continue to next pattern
      }
    }
  }

  // Strategy 4: Try native Date parsing for more complex formats
  try {
    const nativeDate = new Date(strValue);
    // Check if it's a valid date and not "Invalid Date"
    if (!isNaN(nativeDate.getTime()) && nativeDate.getFullYear() >= 1900 && nativeDate.getFullYear() <= 2100) {
      return nativeDate.toLocaleDateString();
    }
  } catch (e) {
    // Continue to fallback
  }

  // Strategy 5: If nothing works, return original value
  return strValue;
};

const MappingPreview = ({
  previewRows,
  previewContactIndex,
  setPreviewContactIndex,
  mapping,
  dateTimeFlags,
  originalHeaders,
}: {
  previewRows: String[][];
  previewContactIndex: number;
  setPreviewContactIndex: (value: number) => void;
  mapping: ContactMapping;
  dateTimeFlags: Record<string, boolean>;
  originalHeaders: String[];
}) => {
  const classes = useStyles();
  
  // Use originalHeaders to correctly map cells by column index
  // This handles:
  // 1. Duplicate column headers (e.g., two "Phone" columns) - originalHeaders preserves order
  // 2. Sparse arrays with holes (empty cells) - for loop doesn't skip holes like Array.map() does
  const mappedPreviewRows = previewRows.map((row) => {
    const dict = {};
    
    // IMPORTANT: Use for loop instead of row.map() because:
    // - Array.map() SKIPS holes in sparse arrays (empty cells become holes)
    // - This causes all subsequent cells to be matched with wrong headers
    // - for loop iterates ALL indices including holes
    const maxIndex = Math.max(row.length, originalHeaders.length);
    for (let index = 0; index < maxIndex; index++) {
      const cell = row[index];
      // Use the original header at this index (preserves duplicates)
      const headerKey = originalHeaders[index]?.toString?.()?.trim?.() ?? '';
      const mappedField = mapping[headerKey];

      // Format dates/timestamps based on field type or custom-dateTime flag
      if (mappedField?.toString?.().startsWith('Custom') && dateTimeFlags[headerKey]) {
        dict[mappedField] = parseSmartTimestamp(cell?.toString() ?? '');
      } else {
        dict[mappedField] = cell;
      }
    }
    
    return dict || {};
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        {/* Tool description */}
        <div>
          <p>
            <b>Welcome to the PowerList Mapping Tool</b>
          </p>
          <p>
            Preview the first {previewRows.length} rows of your upload. Once
            reviewed, press <b>complete</b>.
          </p>
        </div>

        {/* Mapping preview page navigator */}
        <div>
          <PreviewPageNavigator
            previewContactIndex={previewContactIndex}
            setPreviewContactIndex={setPreviewContactIndex}
            previewRows={previewRows}
          />
        </div>
      </div>

      <Table className={classes.mappingTable}>
        <TableHead>
          <TableRow className={classes.mappingHeaderRow}>
            <th className={classes.mappingTableMarginsTopLeft}></th>
            <th className={classes.mappingUploadInfoHeader}>Kixie Header</th>
            <th className={classes.mappingUploadInfoHeader}>Preview</th>
            <th className={classes.mappingTableMarginsTopRight}></th>
          </TableRow>
        </TableHead>
        <TableBody className={classes.mappingBodyContainer}>
          {Object.values(KixieLabeledField).map(
            ({ value, label }, index) =>
              index !== 0 && (
                <TableRow
                  className={classes.previewRow}
                  key={'pl-contact-mapping-preview-row-' + index}
                >
                  <TableCell
                    className={classes.mappingTableMargins}
                  ></TableCell>
                  <TableCell className={classes.mappingUploadInfoCell}>
                    {label}
                  </TableCell>
                  <TableCell className={classes.mappingUploadInfoCell}>
                    {mappedPreviewRows[previewContactIndex][value] ?? '-'}
                  </TableCell>
                  <TableCell
                    className={classes.mappingTableMargins}
                  ></TableCell>
                </TableRow>
              )
          )}
        </TableBody>
      </Table>
      <br></br>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <div />
        <div>
          <PreviewPageNavigator
            previewContactIndex={previewContactIndex}
            setPreviewContactIndex={setPreviewContactIndex}
            previewRows={previewRows}
          />
        </div>
        <div />
      </div>
      <br></br>
    </div>
  );
};

export const PowerListMappingTool = ({
  useHeaderRowAsData,
  setUseHeaderRowAsData,
  mapping,
  updateMapping,
  firstSixRows,
  mappingToolPreview,
  previewContactIndex,
  setPreviewContactIndex,
  dateTimeFlags,
  setDateTimeFlags,
}: {
  useHeaderRowAsData: boolean;
  setUseHeaderRowAsData: (value: boolean) => void;
  mapping: ContactMapping;
  updateMapping: (_event: any, element: any) => void;
  firstSixRows: String[][];
  mappingToolPreview: boolean;
  previewContactIndex: number;
  setPreviewContactIndex: (value: number) => void;
  dateTimeFlags: Record<string, boolean>;
  setDateTimeFlags: (value: Record<string, boolean>) => void;
}) => {
  const numHeadersUploaded = Object.keys(mapping).length;
  const numHeadersWithNoMappings = Object.keys(mapping).filter(
    (key) => mapping[key] === ''
  ).length;

  const previewRows = useHeaderRowAsData
    ? firstSixRows.slice(0, 5)
    : firstSixRows.slice(1, 6);
  
  // The original header row from the file (first row)
  // Used by MappingTool and MappingPreview to correctly map cells by column index
  // even when there are duplicate header names
  const originalHeaders = firstSixRows[0] ?? [];

  const toggleDateTimeFlag = (header: string) => {
    setDateTimeFlags({ ...dateTimeFlags, [header]: !dateTimeFlags[header] });
  };

  return mappingToolPreview === false ? (
    <MappingTool
      numHeadersUploaded={numHeadersUploaded}
      numHeadersWithNoMappings={numHeadersWithNoMappings}
      mapping={mapping}
      setUseHeaderRowAsData={setUseHeaderRowAsData}
      useHeaderRowAsData={useHeaderRowAsData}
      updateMapping={updateMapping}
      previewRows={previewRows}
      dateTimeFlags={dateTimeFlags}
      toggleDateTimeFlag={toggleDateTimeFlag}
      originalHeaders={originalHeaders}
    />
  ) : (
    <MappingPreview
      previewRows={previewRows}
      previewContactIndex={previewContactIndex}
      setPreviewContactIndex={setPreviewContactIndex}
      mapping={mapping}
      dateTimeFlags={dateTimeFlags}
      originalHeaders={originalHeaders}
    />
  );
};
